import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';

type PaymentLinks = Record<string, string>;

type RedirectStatus = 'loading' | 'unavailable' | 'error';

type EvPaymentRedirectProps = {
  lang: Locale.Keys;
};

const REDIRECT_FALLBACK_DELAY_MS = 15000;

const getPaymentUrl = (value: unknown): string => {
  if (typeof value !== 'string' || !value.trim()) {
    return '';
  }

  try {
    const url = new URL(value.trim());
    return url.protocol === 'https:' ? url.toString() : '';
  } catch {
    return '';
  }
};

const EvPaymentRedirect = ({ lang }: EvPaymentRedirectProps) => {
  const { chargerId } = useParams<{ chargerId: string }>();
  const [status, setStatus] = useState<RedirectStatus>('loading');

  useEffect(() => {
    const existingRobotsMeta = document.querySelector<HTMLMetaElement>(
      'meta[name="robots"]'
    );
    const robotsMeta =
      existingRobotsMeta || document.createElement('meta');
    const previousContent = existingRobotsMeta?.getAttribute('content');

    if (!existingRobotsMeta) {
      robotsMeta.setAttribute('name', 'robots');
      document.head.appendChild(robotsMeta);
    }

    robotsMeta.setAttribute('content', 'noindex,nofollow');

    return () => {
      if (!existingRobotsMeta) {
        robotsMeta.remove();
        return;
      }

      if (previousContent) {
        existingRobotsMeta.setAttribute('content', previousContent);
      } else {
        existingRobotsMeta.removeAttribute('content');
      }
    };
  }, []);

  useEffect(() => {
    let isCurrentRequest = true;
    let didLeavePage = false;
    let redirectFallbackTimer: number | undefined;

    const handlePageHide = () => {
      didLeavePage = true;

      if (redirectFallbackTimer) {
        window.clearTimeout(redirectFallbackTimer);
      }
    };

    const showFallbackIfStillHere = () => {
      if (isCurrentRequest && !didLeavePage) {
        setStatus('unavailable');
      }
    };

    window.addEventListener('pagehide', handlePageHide);

    const redirectToPayment = async () => {
      if (!chargerId) {
        setStatus('unavailable');
        return;
      }

      let paymentLinks: PaymentLinks;

      try {
        const response = await fetch(
          `${process.env.PUBLIC_URL}/ev-payment-links.json`,
          { cache: 'no-store' }
        );
        if (!response.ok) {
          throw new Error('Unable to load EV payment links.');
        }

        paymentLinks = (await response.json()) as PaymentLinks;
      } catch {
        if (isCurrentRequest) {
          setStatus('error');
        }
        return;
      }

      const paymentUrl = getPaymentUrl(paymentLinks[chargerId]);

      if (!paymentUrl) {
        if (isCurrentRequest) {
          setStatus('unavailable');
        }
        return;
      }

      try {
        redirectFallbackTimer = window.setTimeout(
          showFallbackIfStillHere,
          REDIRECT_FALLBACK_DELAY_MS
        );
        window.location.replace(paymentUrl);
      } catch {
        if (redirectFallbackTimer) {
          window.clearTimeout(redirectFallbackTimer);
        }

        showFallbackIfStillHere();
      }
    };

    redirectToPayment();

    return () => {
      isCurrentRequest = false;
      window.removeEventListener('pagehide', handlePageHide);

      if (redirectFallbackTimer) {
        window.clearTimeout(redirectFallbackTimer);
      }
    };
  }, [chargerId]);

  const title =
    status === 'loading'
      ? lang.evPaymentRedirect.redirectingTitle
      : lang.evPaymentRedirect.unavailableTitle;

  return (
    <main className='ev_redirect_page'>
      <section className='ev_redirect_content' aria-live='polite'>
        {status === 'loading' && <div className='ev_redirect_spinner' />}
        <p className='ev_redirect_label'>
          {lang.evPaymentRedirect.chargerLabel} {chargerId || '-'}
        </p>
        <h1>{title}</h1>
        {status === 'loading' ? (
          <p>{lang.evPaymentRedirect.loadingMessage}</p>
        ) : (
          <>
            <p>{lang.evPaymentRedirect.contactReception}</p>
            {status === 'error' && (
              <p className='ev_redirect_hint'>
                {lang.evPaymentRedirect.configError}
              </p>
            )}
            <Link className='ev_redirect_link' to='/'>
              {lang.evPaymentRedirect.homeLink}
            </Link>
          </>
        )}
      </section>
    </main>
  );
};

export default EvPaymentRedirect;
